// Copyright (c) Docugami, Inc. All rights reserved.

import { ChunkInstance } from "networking/api";
import { useEffect, useMemo } from "react";
import { folderIdSelector } from "store/dgml/dgmlSelectors";
import { selectAllBindings } from "store/reports/reportsSlice";
import { selectEntries, selectEntryByLabelId, selectOrderedReportEntryIds, selectReportDocumentId, selectReportEntryBindings, selectReportEntryBindingsForCurrentDocument, selectSelectedLabelState } from "store/reports/selectors";
import { selectCurrentChunkInstance } from "store/sdvSelectors";
import { getChunkLabel } from "./LabelChunk";
import { useAppDispatch, useAppSelector } from "./redux";
import { setSelectedLabel } from "components/reports/reportsActions";

export function useReportEntryForSelectedLabel() {
    const documentId = useAppSelector(selectReportDocumentId);
    const folderId = folderIdSelector(documentId);

    // Selected label state
    const selectedLabel = useAppSelector(selectSelectedLabelState);
    const entryForSelectedLabel = useAppSelector(state => selectEntryByLabelId(state, selectedLabel?.id ?? ''));
    const labelEntryBindings = useAppSelector(state => selectReportEntryBindings(state, folderId?.fileId)?.entities[entryForSelectedLabel?.id ?? '']);

    // Selected chunk state
    const selectedInstance = useAppSelector(selectCurrentChunkInstance);
    const entryIdForSelectedInstance = useReportEntryIdForChunk(selectedInstance);
    const entryForSelectedInstance = useAppSelector(state => selectEntries(state)?.entities[entryIdForSelectedInstance ?? '']);

    const dispatch = useAppDispatch();

    useEffect(() => {
        // Preserve the selected label if it is being edited, or if the chunk selection change was a suggestion for this label
        if (!!selectedLabel?.isEditing ||
            !!labelEntryBindings?.suggestions?.find(x => x.spanId === selectedInstance?.spanId) ||
            !!labelEntryBindings?.bindings.find(x => x.spanId === selectedInstance?.spanId)) {
            return;
        }

        // A chunk can be bound to an entry with an unconfirmed label, so use any existing entry for this chunk from bindings
        let label: string = "";
        if (!!selectedInstance) {
            label = !!entryForSelectedInstance ? entryForSelectedInstance.title : dispatch(getChunkLabel(selectedInstance));
        }

        dispatch(setSelectedLabel(label, folderId?.clusterId));
    }, // eslint-disable-next-line react-hooks/exhaustive-deps  
        [selectedInstance])

    return useMemo(() => ({
        entry: entryForSelectedLabel,
        instance: entryForSelectedInstance === entryForSelectedLabel ? selectedInstance : undefined
    }), [entryForSelectedLabel, entryForSelectedInstance, selectedInstance])
}

export function useReportEntryIdForChunk(instance: string | null | ChunkInstance | undefined): string | undefined {
    const documentBindings = useAppSelector(selectReportEntryBindingsForCurrentDocument);
    const entryIds = useAppSelector(selectOrderedReportEntryIds);

    return useMemo(() => {
        if (!!instance && !!documentBindings) {
            const targetInstanceId = !!instance ? ((typeof instance === "string") ? instance : instance.spanId!) : undefined;
            if (!targetInstanceId) {
                return;
            }

            const target = selectAllBindings(documentBindings).find(x => !!x && !!entryIds?.includes(x.entryId) && !!x.bindings.find(y => targetInstanceId === y.spanId))
            return target?.entryId
        }
    }, [documentBindings, instance, entryIds])
}